import React, { FunctionComponent, useState } from 'react';
import { AgEconomicGroup } from '@indigo-ag/schema';
import { faIdCardAlt } from '@fortawesome/pro-regular-svg-icons';
import { EconomicGroupsList, EconomicGroupsListProps } from './EconomicGroupList/EconomicGroupList';
import { Button, Input, SmallBody } from '@indigo-ag/component-module';

import i18next from 'core/i18n';
import { GridContainer, PhaseSection, text } from 'shared';

export interface EconomicGroupProps extends EconomicGroupsListProps {
  economicGroups: AgEconomicGroup[];
}

const validCpfCnpj = /(^\d{14}$|^\d{11}$)/;
const isValidNumber = /^\d*$/;
const initialEconomicGroup: AgEconomicGroup = { name: '', cpfCnpj: '' };

export const EconomicGroup: FunctionComponent<EconomicGroupProps> = props => {
  const { economicGroups, readonly = false, onUpdateEconomicGroup } = props;

  /**
   * State
   */
  const [isCreatingEG, setIsCreatingEG] = useState(false);
  const [isUpdatingEG, setIsUpdatingEG] = useState(false);
  const [newEconomicGroup, setEconomicGroup] = useState<AgEconomicGroup>(initialEconomicGroup);

  const updateNewEG = (
    { target: { value } }: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof AgEconomicGroup
  ) => {
    setEconomicGroup({
      ...newEconomicGroup,
      [fieldName]: fieldName === 'cpfCnpj' ? (isValidNumber.test(value) ? value : newEconomicGroup[fieldName]) : value
    });
  };

  const saveNewEconomicGroup = () => {
    if (onUpdateEconomicGroup) {
      setIsUpdatingEG(true);
      onUpdateEconomicGroup([...economicGroups, newEconomicGroup])
        .then(() => setEconomicGroup(initialEconomicGroup))
        .finally(() => setIsUpdatingEG(false));
    }
  };
  const isNameEmpty = !newEconomicGroup?.name?.trim().length;
  const cpfCnpjValue = newEconomicGroup.cpfCnpj?.trim();

  const shouldDisableSave = isNameEmpty || !cpfCnpjValue?.length || !validCpfCnpj.test(cpfCnpjValue);

  return (
    <PhaseSection title={i18next.t('economicGroup')}>
      {economicGroups && economicGroups.length ? (
        <div className="mt-4 mb-4">
          <EconomicGroupsList {...props} />
        </div>
      ) : (
        <SmallBody>{text.firstLetterUppercase(i18next.t('noEconomicGroups'))}</SmallBody>
      )}
      <div className="mt-4">
        {!readonly ? (
          isCreatingEG ? (
            <>
              <GridContainer columns={2}>
                <Input
                  label={text.toTitleCase(i18next.t('name'))}
                  value={newEconomicGroup?.name}
                  onChange={event => updateNewEG(event, 'name')}
                  placeholder={text.firstLetterUppercase(i18next.t('writeGroupNameHere'))}
                  sizeVariant="slim"
                />
                <Input
                  label={text.toTitleCase(i18next.t('cpf/cnpj'))}
                  value={newEconomicGroup?.cpfCnpj}
                  onChange={event => updateNewEG(event, 'cpfCnpj')}
                  placeholder={text.firstLetterUppercase(i18next.t('writeGroupCpfHere'))}
                  sizeVariant="slim"
                  maxLength={14}
                  faIconBefore={faIdCardAlt}
                />
              </GridContainer>
              <Button variant="secondary" onClick={() => setIsCreatingEG(false)}>
                {i18next.t('cancel')}
              </Button>
              <Button disabled={shouldDisableSave} onClick={saveNewEconomicGroup} busy={isUpdatingEG}>
                {i18next.t('save')}
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsCreatingEG(true)} className="text-transform-capitalize">
              {text.firstLetterUppercase(i18next.t('newEconomicGroup'))}
            </Button>
          )
        ) : null}
      </div>
    </PhaseSection>
  );
};
